import React, { useState } from 'react';

import {
  Button,
  Divider,
  Grid,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Form, Formik } from 'formik';
import { TranslateFunction } from 'react-localize-redux';

import { Modal } from '@fd/ui/Modal/Modal';
import { ModalActions } from '@fd/ui/Modal/ModalActions';
import ModalButtons from '@fd/ui/Modal/ModalButtons';
import { ModalContent } from '@fd/ui/Modal/ModalContent';
import { FormikInputField } from '@fd/ui/molecules';

import { KEYWORD_MIN_LENGTH, KEYWORDS_MAX_LENGTH } from '../../constants';
import { KeywordsListBox } from '../KeywordsListBox';

type TextFieldErrors = {
  TextField: string;
};

const validateKeywordsForm = (
  values,
  translate: TranslateFunction,
  stateKeywords: string[] = []
): Partial<TextFieldErrors> => {
  const { TextField } = values;
  const errors: Partial<any> = {};
  const trimmedTextField = TextField?.trim();
  if (TextField.length > 0 && trimmedTextField.length < KEYWORD_MIN_LENGTH) {
    errors.TextField = translate('Min_fieldname_length_exceeded', {
      fieldName: '',
      min: KEYWORD_MIN_LENGTH,
    }) as string;
  }

  if (trimmedTextField.includes(',')) {
    errors.TextField = translate('keyword_should_not_contain_commas') as string;
  }

  if (stateKeywords.includes(trimmedTextField)) {
    errors.TextField = translate('keyword_already_exists');
  }
  return errors;
};
const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    margin: theme.spacing(1),
    height: '32px',
    borderRadius: '16px',
    backgroundColor: '#EAF2FF',
    color: theme.palette.grey[700],
  },
  addButton: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(1),
      width: '28%',
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(4),
    },
  },
  keywordTextField: {
    [theme.breakpoints.down('sm')]: {
      width: '68%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  divider: {
    marginTop: theme.spacing(2),
  },
}));

export type EditkeywordsModalProps = {
  open: boolean;
  keywords: string[];
  setParentKeywords: (keywords: any) => void;
  translate: TranslateFunction;
  setOpen: (open: boolean) => void;
  keywordsDescription: JSX.Element;
};
export const EditkeywordsModal = (props: EditkeywordsModalProps) => {
  const { open, keywords, setParentKeywords, translate, setOpen, keywordsDescription } = props;
  const [stateKeywords, setStatekeywords] = useState<string[]>(keywords);
  const classes = useStyles();

  const onCancel = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setParentKeywords(stateKeywords);
    setOpen(false);
  };

  const handleDeleteKeyword = (keyword) => {
    const newKeyords = stateKeywords.filter((word) => word !== keyword);
    setStatekeywords(newKeyords);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <Modal maxWidth="700px" titleTextId={'Edit_keyword_list'} open={open} onClose={onCancel}>
      <ModalContent>
        {keywordsDescription}
        <Formik
          initialValues={{
            TextField: '',
          }}
          onSubmit={(values, { resetForm }) => {
            setStatekeywords([...stateKeywords, values.TextField.trim()]);
            resetForm();
          }}
          validate={(values) => {
            return validateKeywordsForm(values, translate, stateKeywords);
          }}
        >
          {({ dirty }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <KeywordsListBox
                    handleDeleteKeyword={handleDeleteKeyword}
                    clearKeywords={() => setStatekeywords([])}
                    isEdit={true}
                    translate={translate}
                    keywords={stateKeywords}
                    title={translate('Keywords') as string}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikInputField
                    className={classes.keywordTextField}
                    helperText={' '}
                    fullWidth={false}
                    variant="outlined"
                    fdKey={'add-custom-word-field'}
                    name="TextField"
                    label={translate('Add_custom_word') as string}
                  />
                  <Button
                    className={classes.addButton}
                    color="primary"
                    variant="outlined"
                    data-fd="add-to-keywords-list"
                    disabled={!dirty}
                    type="submit"
                  >
                    {translate('Add')}
                  </Button>
                  {!isMobile && <Divider className={classes.divider} />}
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </ModalContent>
      <ModalActions>
        <ModalButtons
          leftButtonTranslationId="Cancel"
          leftButtonAction={() => setOpen(false)}
          rightButtonTranslationId="Save"
          rightButtonAction={handleSave}
          fdKey={'edit-keywords-modal-buttons'}
          rightDisabled={
            stateKeywords.length === 0 || stateKeywords.join(',').length > KEYWORDS_MAX_LENGTH
          }
        />
      </ModalActions>
    </Modal>
  );
};
